import React from 'react';
import {
  Grid,
  FormHelperText,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { Info as InfoIcon, Save as SaveIcon } from '@mui/icons-material';

import {
  HoneyGeneralContainer,
  HoneyGeneralHeader,
  HoneyGeneralTitle,
  HoneyGeneralSubtitle,
  HoneyFormContainer,
  HoneyTextField,
  HoneyFormControl,
  HoneyRequired,
  HoneyActionsContainer,
  HoneySaveButton,
  HoneyCancelButton
} from './styled';

const GeneralInfoView = ({
  formData,
  errors,
  handleInputChange,
  getCapacityLabel,
  getEntityLabel,
  isWorkAreaMode,
  canActivate,
  // NEW: Save/Cancel functionality
  hasChanges = false,
  isSaving = false,
  onSave,
  onCancel,
  showSaveButton = false
}) => {
  const entityLabel = getEntityLabel();
  const capacityLabel = getCapacityLabel();

  const getStatusHelpText = () => {
    switch (formData.status) {
      case 'AC':
        return `Active ${entityLabel}s are available for booking`;
      case 'IN':
        return `Inactive ${entityLabel}s won't be shown for booking`;
      default:
        return `Inactive ${entityLabel}s won't be shown for booking`;
    }
  };

  return (
    <HoneyGeneralContainer>
      <HoneyGeneralHeader>
        <HoneyGeneralTitle>
          <InfoIcon />
          General Information
        </HoneyGeneralTitle>
        <HoneyGeneralSubtitle>
          Manage the basic details of this {entityLabel}
        </HoneyGeneralSubtitle>
      </HoneyGeneralHeader>

      <HoneyFormContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HoneyTextField
              fullWidth
              label={
                <>
                  {entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)} Name <HoneyRequired>*</HoneyRequired>
                </>
              }
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              placeholder={`Enter ${entityLabel} name`}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <HoneyTextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder={`Describe this ${entityLabel}...`}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <HoneyTextField
              fullWidth
              label={
                <>
                  {capacityLabel} <HoneyRequired>*</HoneyRequired>
                </>
              }
              name="capacity"
              type="number"
              value={formData.capacity || ''}
              onChange={handleInputChange}
              error={!!errors.capacity}
              helperText={
                errors.capacity || 
                (capacityLabel === 'Capacity' 
                  ? "Maximum number of people" 
                  : "Number of hot desks available"
                )
              }
              inputProps={{ 
                min: isWorkAreaMode ? 0 : 1, 
                max: 50 
              }}
              placeholder={
                capacityLabel === 'Capacity' 
                  ? "Max people" 
                  : "Number of hot desks"
              }
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip 
              title={!canActivate ? "This item cannot be activated due to missing requirements" : ""}
              placement="top"
            >
              <HoneyFormControl fullWidth>
                <InputLabel>
                  Status <HoneyRequired>*</HoneyRequired> {!canActivate && formData.status === 'AC' && '⚠️'}
                </InputLabel>
                <Select
                  name="status"
                  value={formData.status || 'IN'}
                  onChange={handleInputChange}
                  label={`Status ${!canActivate && formData.status === 'AC' ? '⚠️' : ''}`}
                  error={!canActivate && formData.status === 'AC'}
                >
                  <MenuItem value="AC" disabled={!canActivate}>
                    Active
                  </MenuItem>
                  <MenuItem value="IN">Inactive</MenuItem>
                </Select>
                <FormHelperText 
                  error={!canActivate && formData.status === 'AC'}
                >
                  {getStatusHelpText()}
                </FormHelperText>
              </HoneyFormControl>
            </Tooltip>
          </Grid>
        </Grid>

        {/* NEW: Save/Cancel Actions */}
        {showSaveButton && (
          <HoneyActionsContainer>
            {hasChanges && !isSaving && (
              <HoneyCancelButton
                variant="outlined"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </HoneyCancelButton>
            )}
            
            <HoneySaveButton
              variant="contained"
              onClick={onSave}
              disabled={!hasChanges || isSaving}
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </HoneySaveButton>
          </HoneyActionsContainer>
        )}
      </HoneyFormContainer>
    </HoneyGeneralContainer>
  );
};

export default GeneralInfoView;